import { Component, inject, OnInit, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { ConfirmationModalComponent, DataTableComponent, FormActionButtonsComponent } from "../../../shared/components";
import { SendConfirmationModalComponent } from "../../../shared/components/general/send-confirmation-modal/send-confirmation-modal";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean } from "../../../shared/constants/enums";
import {
  CustomButton,
  EntityMapModelType,
  ItemAction,
  TableButtonClicked,
  TableInputChangedOutput,
  TableInputs,
} from "../../../shared/models";
import { CyclesApiService, LanguageService, SystemLayoutService } from "../../../shared/services";

@Component({
  selector: "adaa-strategic-cycles",
  imports: [DataTableComponent, FormActionButtonsComponent, TranslatePipe],
  templateUrl: "./strategic-cycles-list.html",
  styleUrl: "./strategic-cycles-list.scss",
})
export class StrategicCyclesListComponent implements OnInit {
  private _router = inject(Router);
  private _cyclesApiService = inject(CyclesApiService);
  private _modalService = inject(NgbModal);
  private _toastrService = inject(ToastrService);
  private _languageService = inject(LanguageService);
  private _systemLayoutService = inject(SystemLayoutService);

  sendInvite = signal<boolean>(false);
  planEntities = signal<EntityMapModelType[]>([]);
  allSent = signal<boolean>(false);

  adaaDataTable = viewChild.required<DataTableComponent>("adaaDataTable");

  Constants = Constants;
  hideDeleteRules: ItemAction[] = [
    {
      operator: "equal",
      propertyName: "status",
      compareWith: Constants.OBJECT_STATUS.ARCHIVED,
    },
    {
      operator: "equal",
      propertyName: "status",
      compareWith: Constants.OBJECT_STATUS.ACTIVE,
    },
    {
      operator: "equal",
      propertyName: "status",
      compareWith: Constants.OBJECT_STATUS.CLOSED,
    },
  ];

  hideEditRules: ItemAction[] = [
    {
      operator: "equal",
      propertyName: "status",
      compareWith: Constants.OBJECT_STATUS.ARCHIVED,
    },
  ];

  customButtons: CustomButton[] = [
    {
      iconName: "adaa-icon-float-email",
      eventName: "send",
      iconTitle: "common.form.button.send_to_entities",
      onlyIf: (data: { status: number }) => data?.status !== Constants.OBJECT_STATUS.ARCHIVED,
    },
    {
      iconName: "adaa-icon-edit-disabled",
      eventName: "editDisabled",
      iconTitle: "tooltips.edit_disabled",
      onlyIf: (data: { status: number }) => data?.status === Constants.OBJECT_STATUS.ARCHIVED,
    },
  ];

  tableInputs: TableInputs[] = [
    {
      fieldName: "flag",
      checkboxMode: "yesNo",
      disableRules: [
        {
          propertyName: "wasSent",
          compareWith: "Y",
          operator: "equal",
        },
      ],
    },
  ];

  untilDestroyed = AdaaHelper.untilDestroyed();

  public ngOnInit(): void {
    this._systemLayoutService.hasCycleChanged$.pipe(this.untilDestroyed()).subscribe({
      next: () => this.adaaDataTable().loadTableData(true),
    });

    this._systemLayoutService.hasActiveEntityChanged$.subscribe(() => {
      !AdaaHelper.isPMOEntity() && this._router.navigateByUrl("/console");
    });
  }

  public addNewClicked() {
    this._router.navigateByUrl("/console/cycles/create");
  }

  private _getPlan(planId: number) {
    this._cyclesApiService.getByPlanId(planId).subscribe({
      next: (response) => {
        response.responseData.forEach((e) => (e.flag = AdaaBoolean.Y));
        response.responseData.forEach((e) => (e.status = Constants.OBJECT_STATUS.ACTIVE));

        this.planEntities.set(response.responseData);
        if (!response.responseData.find((entity) => entity.wasSent === AdaaBoolean.N)) {
          this.allSent.set(true);
        }
      },
    });
  }

  private _deletePlan(planId: number) {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = "cycle.delete_yes_no_title";
    modal.componentInstance.title = "cycle.delete_yes_no_information";

    modal.result.then((e) => {
      if (e) {
        this._cyclesApiService.deletePlan(planId).subscribe({
          next: () => {
            this._toastrService.success(this._languageService.translate("notification.success.remove"));
            this.adaaDataTable().loadTableData();
          },
        });
      }
    });
  }

  public getAction(value: TableButtonClicked) {
    switch (value.event) {
      case "view":
        this._router.navigateByUrl(`/console/cycles/${value.data.id}`);
        break;
      case "edit":
        this._router.navigateByUrl(`/console/cycles/edit/${value.data.id}`);
        break;
      case "send":
        this.sendInvite.set(true);
        this._getPlan(value.data.id);
        break;
      case "delete":
        this._deletePlan(value.data.id);
        break;

      default:
        break;
    }
  }

  public sendToEntities() {
    const planEntities = this.planEntities();

    const modal = this._modalService.open(SendConfirmationModalComponent, {
      centered: true,
      size: "lg",
      modalDialogClass: this._languageService.modalDirection(),
    });

    const toSend = planEntities
      .filter((e) => e.flag === AdaaBoolean.Y && e.wasSent === AdaaBoolean.N)
      .map((e) => AdaaHelper.getItemValueByToken(e, "entityName"));

    modal.componentInstance.header = "cycle.notify_entities_confirm";
    modal.componentInstance.title = "common.tables.entity_name";
    modal.componentInstance.items = toSend;

    modal.result.then((e) => {
      if (e) {
        this._cyclesApiService.sendToEntities(planEntities).subscribe({
          next: () => {
            this._toastrService.success(this._languageService.translate("notification.success.save"));
            this.close();
          },
        });
      }
    });
  }

  public formatEntities(values?: TableInputChangedOutput[]) {
    if (!values) return;

    const planEntities = this.planEntities();

    for (const value of values) {
      const index = planEntities.findIndex((e) => e.id === value.data.id);
      if (index !== -1) {
        planEntities[index] = {
          ...planEntities[index],
          flag: value.value,
          status: value.value === AdaaBoolean.Y ? Constants.OBJECT_STATUS.ACTIVE : Constants.OBJECT_STATUS.PLANNING,
        };
      }
    }

    this.planEntities.set([...planEntities]);
  }

  public close() {
    this.sendInvite.set(false);
    this.allSent.set(false);
  }
}
